import { ReactNode, createContext, useEffect, useState } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useNavigate } from 'react-router';
import { useAuthToken } from '@repo/hooks/useAuthToken';
import { usePath } from '@repo/hooks/usePath';
import { UserLoader } from './UserLoader';
import { LoadingError } from '../Elements/Errors';
import { Mercury } from '@repo/types/Models';

const GET_USER_Q = gql(`
  query MyUser {
    user: myUser {
      id

      email
      insertedAt
      updatedAt
      name
      first_name
      last_name
      account {
        id
        name
        customerId
   
        name
        notes

        insertedAt
        updatedAt
        activeAt
        expireAt
     
        deactivatedAt

        smsAccess
        mmsAccess
        lrnAccess

        requireIpApiAccess
        requireIpGqlAccess

        defaultExpiresIn
        legacyAccess
        maxSubAccounts

        override_max_sub_accounts
        override_max_dids
        override_legacy_access
        override_lrn_access
        override_sms_access
        override_mms_access
        override_require_ip_api_access
        override_require_ip_gql_access    
        override_require_did_group_sms_access
        override_require_did_group_mms_access
        override_require_did_group_approval

        requireDidGroupSmsAccess
        requireDidGroupMmsAccess
        requireDidGroupApproval
            
        supportPlan {
          id
          name
        }
        servicePlan {
          id
          name
          notes
          insertedAt
          updatedAt
        }
      }
      role {
        id
        name
        insertedAt
        updatedAt

        resources {
          resourceId
          scope
          actions {
            name
            value
          }
        }
      }
    }
  }
`);

export interface MyUser {
  id: string;
  email: string;
  insertedAt: Date;
  updatedAt: Date;
  name: string;
  first_name: string;
  last_name: string;
  mfaTotpEnabled: boolean;
  account: Mercury.Models.Account;
  role: {
    id: string;
    name: string;
    insertedAt: Date;
    active: boolean;
    updatedAt: Date;

    resources: Mercury.Models.RoleResource[];
  };
}

const context = createContext<MyUser | null>(null);
const { Provider } = context;

export const UserContextContext = context;

export interface UserProps {
  children?: ReactNode;
}

const toLogin = (path: string, navigate: ReturnType<typeof useNavigate>) => {
  // Check if manual logout was triggered (skip redirect)
  const skipRedirect = localStorage.getItem('mercury_skip_redirect') === 'true';
  localStorage.removeItem('mercury_skip_redirect'); // Clear the flag

  let target = '/login';
  if (!skipRedirect && path !== '/') {
    const params = new URLSearchParams();
    params.set('redirect_to', path);
    target = `${target}?${params.toString()}`;
  }
  navigate(target);
};

const ClientUserContext = ({ children }: UserProps) => {
  const [token] = useAuthToken();
  const navigate = useNavigate();
  const path = usePath();
  useEffect(() => {
    if (token) return;
    toLogin(path, navigate);
  }, [path, navigate, token]);
  return token ? (
    <AfterTokenClientUserContext>{children}</AfterTokenClientUserContext>
  ) : (
    <UserLoader />
  );
};

const AfterTokenClientUserContext = ({ children }: UserProps) => {
  const navigate = useNavigate();
  const path = usePath();
  const setToken = useAuthToken()[1];
  const { error, data, loading, refetch } = useQuery(GET_USER_Q, {
    errorPolicy: 'ignore',
  });
  // Apollo 4 returns `data` typed as the document's data shape OR a deep-partial
  // when the document declares `@unmask` / progressive results; cast to the
  // narrow shape we actually consume.
  const user = (data as { user?: MyUser } | undefined)?.user ?? null;
  useEffect(() => {
    if (loading) return;
    if (!user) {
      setToken(null);
      toLogin(path, navigate);
    }
  }, [path, navigate, setToken, loading, user]);
  if (error) {
    return <LoadingError error={error} refresh={refetch} />;
  }
  if (!user) {
    return <UserLoader />;
  }
  return <Provider value={user}>{children}</Provider>;
};

const ServerUserContext = () => {
  // Maybe it would be good to show something different on server, but I am uncertain
  // How to implement that best.
  return <UserLoader />;
};

export const UserContext = ({ children }: UserProps) => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => setIsClient(true), []);
  return isClient ? <ClientUserContext>{children}</ClientUserContext> : <ServerUserContext />;
};
